<script setup lang="ts">
import type { SelectOption } from '@camelot/components'
import { CtAutoComplete } from '@camelot/components'
import { computedAsync, useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'
import { watch } from 'vue'
import { type AirportZoneListRto, type AirportZoneRto, ExternalApi } from '~/api/location'
import { useGoogleAPI } from '~/composables/google-api'
import { translate, useTranslations } from '~/composables/translate/translations'
import { useLocationStore } from '~/stores/location'
import { mapAirportZoneToSelectOption } from '~/composables/airport-mapper'
import type { Address } from '~/types/location'

// @ts-expect-error Say thanks Google Inc for such a beautiful code
import AutocompletePrediction = google.maps.places.AutocompletePrediction

// @ts-expect-error Say thanks Google Inc for such a beautiful code
import AutocompletionRequest = google.maps.places.AutocompletionRequest

// @ts-expect-error Say thanks Google Inc for such a beautiful code
import AutocompleteResponse = google.maps.places.AutocompleteResponse

const locationAPI = new ExternalApi()

const { autoCompleteService } = useGoogleAPI()

const locationStore = useLocationStore()
const { city, addressOptions, address, addressError } = storeToRefs(locationStore)

watch(city, (_) => {
  useRouter().replace({ params: { _city: city.value.id } })
  addressOptions.value.clear()
  address.value = undefined
})

const airportsOption = computedAsync<SelectOption[]>(async () => {
  try {
    const result: AirportZoneListRto = await locationAPI.getAirportZoneList({ locationId: city.value.locationId })
    const airports: Array<AirportZoneRto> = result.airportZoneList ?? new Array<AirportZoneRto>()
    return airports.map((airport: AirportZoneRto) => mapAirportZoneToSelectOption(airport))
  }
  catch (error) {
    Sentry.captureMessage(
        `Error while fetching airport Zone List ✈️🛩️`,
        { level: 'error', extra: { city: city.value }, tags: { operation: 'get-airport-zone-list' } },
    )
    console.error('Error while fetching airport', error)
    return new Array<SelectOption>()
  }
}, [])

function buildRequest(query: string): AutocompletionRequest {
  return {
    language: useTranslations().language.value,
    input: query,
    locationBias: new google.maps.Circle({
      radius: city.value.zoneArea.radius,
      center: { lat: city.value.zoneArea.center.latitude, lng: city.value.zoneArea.center.longitude },
    }),
    componentRestrictions: { country: city.value.country },
    types: ['address'],
  }
}

async function predictAddress(query?: string) {
  addressOptions.value.clear()
  if (!autoCompleteService.value || !query) {
    airportsOption.value.forEach(airport => addressOptions.value.add(airport))
    return
  }

  try {
    const response: AutocompleteResponse = await autoCompleteService.value.getPlacePredictions(buildRequest(query))
    if (response) {
      response.predictions.forEach((pred: AutocompletePrediction) => {
        const value: Address = { info: pred.description }
        addressOptions.value.add({ label: pred.description, value })
      })
    }
    else {
      console.error('Could not resolve AirPort (library was not properly loaded or invalid key)')
    }
  }
  catch (error) {
    Sentry.captureMessage(
        `Error while fetching airport ✈️🎒`,
        { level: 'error', extra: { input: query, city: city.value }, tags: { operation: 'predict-address' } },
    )
    console.error('Error while fetching airport', error)
  }
}
const debouncedPredictAddress = useDebounceFn(predictAddress, 200)

function nonFilteringFunction(options: SelectOption[], _query?: string): SelectOption[] {
  // Potentially can be needed in future
  return options
}

function onFocusIn() {
  if (addressOptions.value.size === 0)
    debouncedPredictAddress()
}
</script>

<template>
  <CtAutoComplete
    v-model="address"
    :label="translate('field_label_address_app')"
    :options="[...addressOptions]"
    :messages="addressError"
    :error="!!addressError"
    :placeholder="translate('placeholder_address')"
    :clearable="true"
    prefix-icon="location"
    :filtering-fn="nonFilteringFunction"
    @update:query="debouncedPredictAddress"
    @focusin="onFocusIn"
  />
</template>
